import React from 'react';
import { CheckCircle, Shield, HelpCircle } from 'lucide-react';
import { CmsLink as Link, EditableText } from '../content/EditableText';
import { useCmsContent } from '../content/CmsContentContext';

const Insurance = () => {
  const { content } = useCmsContent();
  const { insuranceProviders } = content;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-brand-700 to-brand-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-light mb-4"><EditableText contentKey="pages.insurance.heroTitle" /></h1>
          <p className="text-xl max-w-3xl mx-auto">
            <EditableText contentKey="pages.insurance.heroBody" />
          </p>
        </div>
      </section>

      {/* Accepted Insurance */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-4"><EditableText contentKey="pages.insurance.acceptedTitle" /></h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            <EditableText contentKey="pages.insurance.acceptedBody" />
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {insuranceProviders.map((provider, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-brand-50 to-white p-8 rounded-lg flex items-center justify-center hover:shadow-lg transition-all duration-300 border-2 border-brand-100 hover:border-brand-300"
              >
                <p className="text-xl font-semibold text-gray-800 text-center"><EditableText contentKey={`insuranceProviders.${index}`} multiline={false} /></p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            <EditableText contentKey="pages.insurance.howTitle" />
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="bg-brand-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <EditableText contentKey="pages.insurance.step1Number" className="text-2xl font-bold text-brand-700" multiline={false} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3"><EditableText contentKey="pages.insurance.step1Title" /></h3>
              <p className="text-gray-600">
                <EditableText contentKey="pages.insurance.step1Body" />
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="bg-brand-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <EditableText contentKey="pages.insurance.step2Number" className="text-2xl font-bold text-brand-700" multiline={false} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3"><EditableText contentKey="pages.insurance.step2Title" /></h3>
              <p className="text-gray-600">
                <EditableText contentKey="pages.insurance.step2Body" />
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="bg-brand-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <EditableText contentKey="pages.insurance.step3Number" className="text-2xl font-bold text-brand-700" multiline={false} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3"><EditableText contentKey="pages.insurance.step3Title" /></h3>
              <p className="text-gray-600">
                <EditableText contentKey="pages.insurance.step3Body" />
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Options */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            <EditableText contentKey="pages.insurance.paymentTitle" />
          </h2>
          <div className="space-y-6">
            <div className="flex items-start p-6 bg-brand-50 rounded-lg">
              <CheckCircle className="h-6 w-6 text-brand-700 mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2"><EditableText contentKey="pages.insurance.selfPayTitle" /></h3>
                <p className="text-gray-700">
                  <EditableText contentKey="pages.insurance.selfPayBody" />
                </p>
              </div>
            </div>
            <div className="flex items-start p-6 bg-brand-50 rounded-lg">
              <CheckCircle className="h-6 w-6 text-brand-700 mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2"><EditableText contentKey="pages.insurance.slidingTitle" /></h3>
                <p className="text-gray-700">
                  <EditableText contentKey="pages.insurance.slidingBody" />
                </p>
              </div>
            </div>
            <div className="flex items-start p-6 bg-brand-50 rounded-lg">
              <CheckCircle className="h-6 w-6 text-brand-700 mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2"><EditableText contentKey="pages.insurance.hsaTitle" /></h3>
                <p className="text-gray-700">
                  <EditableText contentKey="pages.insurance.hsaBody" />
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            <EditableText contentKey="pages.insurance.faqTitle" />
          </h2>
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-start">
                <HelpCircle className="h-6 w-6 text-brand-700 mr-3 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    <EditableText contentKey="pages.insurance.faq1Question" />
                  </h3>
                  <p className="text-gray-700">
                    <EditableText contentKey="pages.insurance.faq1Answer" />
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-start">
                <HelpCircle className="h-6 w-6 text-brand-700 mr-3 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    <EditableText contentKey="pages.insurance.faq2Question" />
                  </h3>
                  <p className="text-gray-700">
                    <EditableText contentKey="pages.insurance.faq2Answer" />
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-start">
                <HelpCircle className="h-6 w-6 text-brand-700 mr-3 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    <EditableText contentKey="pages.insurance.faq3Question" />
                  </h3>
                  <p className="text-gray-700">
                    <EditableText contentKey="pages.insurance.faq3Answer" />
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-brand-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-light mb-4"><EditableText contentKey="pages.insurance.ctaTitle" /></h2>
          <p className="text-xl mb-8">
            <EditableText contentKey="pages.insurance.ctaBody" />
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/book-appointment"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-brand-700 rounded-md hover:bg-gray-100 transition-colors text-lg font-medium"
            >
              <EditableText contentKey="pages.insurance.book" multiline={false} />
            </Link>
            <a
              href="tel:5035550123"
              className="inline-flex items-center justify-center px-8 py-3 border-2 border-white text-white rounded-md hover:bg-white hover:text-brand-700 transition-colors text-lg font-medium"
            >
              <EditableText contentKey="pages.insurance.call" multiline={false} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Insurance;
